const USER_KEY = 'user';
const PERMISSION_KEY = 'permission_denied';
const LEGACY_KEYS = [USER_KEY, PERMISSION_KEY];

function notAuthenticatedError() {
    const err = new Error('Not authenticated');
    err.status = 401;
    return err;
}

/**
 * Manages auth state in localStorage.
 *
 * On first access, performs a one-time migration of any existing keys from
 * sessionStorage (previous behavior) so upgrading users stay logged in.
 *
 * All methods are static — import the class and call directly:
 *   AuthStorage.getUser(), AuthStorage.saveUser(user), etc.
 */
export class AuthStorage {
    static #migrated = false;

    /**
     * Returns the underlying localStorage instance, or null when unavailable
     * (SSR / private mode). Performs the sessionStorage migration on first call.
     */
    static getStorage() {
        if (typeof localStorage === 'undefined') return null;
        if (!AuthStorage.#migrated) {
            AuthStorage.#migrated = true;
            try {
                if (typeof sessionStorage !== 'undefined') {
                    for (const k of LEGACY_KEYS) {
                        const v = sessionStorage.getItem(k);
                        if (v != null && localStorage.getItem(k) == null) {
                            localStorage.setItem(k, v);
                            sessionStorage.removeItem(k);
                        }
                    }
                }
            } catch {
                // private mode / storage quota — continue with whatever is available
            }
        }
        return localStorage;
    }

    /**
     * Read and parse the stored user object.
     * Throws a 401 error when no user is stored or the stored value is invalid JSON.
     */
    static getUser() {
        const raw = AuthStorage.getStorage()?.getItem(USER_KEY);
        if (!raw) throw notAuthenticatedError();
        try {
            return JSON.parse(raw);
        } catch {
            throw notAuthenticatedError();
        }
    }

    /** Persist a user object. Logs storage errors (quota / private mode) but does not throw. */
    static saveUser(user) {
        try {
            AuthStorage.getStorage()?.setItem(USER_KEY, JSON.stringify(user));
        } catch (err) {
            console.error('[Mimir auth] Failed to persist user to localStorage:', err?.message);
        }
    }

    /** Remove the stored user object. */
    static clearUser() {
        try {
            AuthStorage.getStorage()?.removeItem(USER_KEY);
        } catch (err) {
            console.error('[Mimir auth] Failed to clear user from storage:', err?.message);
        }
    }

    /** Returns true when the permission-denied flag is set. */
    static isPermissionDenied() {
        return AuthStorage.getStorage()?.getItem(PERMISSION_KEY) === '1';
    }

    /** Reset the migration flag (test-only). */
    static _resetMigrationForTests() {
        AuthStorage.#migrated = false;
    }

    /** Set or clear the permission-denied flag. */
    static setPermissionDenied(value) {
        try {
            const storage = AuthStorage.getStorage();
            if (!storage) return;
            if (value) storage.setItem(PERMISSION_KEY, '1');
            else storage.removeItem(PERMISSION_KEY);
        } catch (err) {
            console.error('[Mimir auth] Failed to update permission-denied flag:', err?.message);
        }
    }
}
